/**
 * Modal form for creating and editing auth status options within the
 * Options Configuration page. Handles validation, color picking, and
 * conditional field display based on add/edit mode.
 * @module authOptionsModal
 */

import { useEffect, useState } from "react";
import {
  closeModal,
  Modal,
  Select,
  TextInput,
  ToggleSwitch,
} from "risa-oasis-ui_v2";
import { AuthStatusOptionModel } from "../../../../../../data-model/authStatusOptions";
import { BOOptionModel } from "../../../../../../data-model/boOptionModel";
import { ModalId } from "../../../../../../../shared/enums/modalId";

/** Props for {@link AuthOptionsModal}. */
interface AuthOptionsModalProps {
  data: AuthStatusOptionModel | null;
  onModalClose: () => void;
  onSave: (option: AuthStatusOptionModel) => Promise<void>;
  mode: "add" | "edit";
  existingOptions: AuthStatusOptionModel[];
  selectedOrgId: string;
  boOptions: BOOptionModel[];
}

/**
 * Renders a modal dialog for adding or editing a single auth status option.
 * In edit mode, priority and left-pane order are shown read-only; all other
 * fields are editable. The component validates required fields and duplicate
 * IDs before persisting via `props.onSave`.
 */
const AuthOptionsModal = (props: AuthOptionsModalProps) => {
  const [text, setText] = useState("");
  const [id, setId] = useState("");
  const [priority, setPriority] = useState<string>("");
  const [leftPaneDisplayOrder, setLeftPaneDisplayOrder] = useState<string>("");
  const [textColor, setTextColor] = useState("");
  const [bgColor, setBgColor] = useState("");
  const [mappedBoValue, setMappedBoValue] = useState("");
  const [markAsCompletedToggle, setMarkAsCompletedToggle] = useState(false);
  const [agentMode, setAgentMode] = useState(false);
  const [processClinicalDocuments, setProcessClinicalDocuments] =
    useState(false);
  const [authLetterType, setAuthLetterType] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<{
    text?: string;
    id?: string;
    priority?: string;
    leftPaneDisplayOrder?: string;
  }>({});

  useEffect(() => {
    if (props.data && props.mode === "edit") {
      setText(props.data.text || "");
      setId(props.data.id || "");
      setPriority(
        props.data.priority !== undefined ? String(props.data.priority) : "",
      );
      setLeftPaneDisplayOrder(
        props.data.left_pane_display_order !== undefined
          ? String(props.data.left_pane_display_order)
          : "",
      );
      setTextColor(props.data.textColor || "");
      setBgColor(props.data.bgColor || "");
      setMappedBoValue(props.data.mapped_bo_value || "");
      setAuthLetterType(props.data.auth_letter_type || "");
      setMarkAsCompletedToggle(props.data.mark_as_completed_toggle ?? false);
      setAgentMode(props.data.agent_mode ?? false);
      setProcessClinicalDocuments(
        props.data.process_clinical_documents ?? false,
      );
    } else {
      setText("");
      setId("");
      setPriority("");
      setLeftPaneDisplayOrder("");
      setTextColor("");
      setBgColor("");
      setMappedBoValue("");
      setAuthLetterType("");
      setMarkAsCompletedToggle(false);
      setAgentMode(false);
      setProcessClinicalDocuments(false);
    }
    setErrors({});
  }, [props.data, props.mode]);

  const isValidPositiveInteger = (value: string): boolean => {
    if (value === "") return true; // Optional field
    const num = Number(value);
    return Number.isInteger(num) && num >= 0;
  };

  const validateForm = (): boolean => {
    const newErrors: {
      text?: string;
      id?: string;
      priority?: string;
      leftPaneDisplayOrder?: string;
    } = {};

    if (!text.trim()) {
      newErrors.text = "Text is required";
    }

    if (!id.trim()) {
      newErrors.id = "ID is required";
    } else {
      // Validate duplicates for both add and edit (excluding current item in edit mode)
      const duplicateId = props.existingOptions.find(
        (o) =>
          o.id.toLowerCase() === id.toLowerCase().trim() &&
          (props.mode === "add" || o.id !== props.data?.id),
      );
      if (duplicateId) {
        newErrors.id = "An auth option with this ID already exists";
      }
    }

    if (!isValidPositiveInteger(priority)) {
      newErrors.priority = "Priority must be a non-negative integer";
    }

    if (!isValidPositiveInteger(leftPaneDisplayOrder)) {
      newErrors.leftPaneDisplayOrder =
        "Left Pane Order must be a non-negative integer";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const option: AuthStatusOptionModel = {
        text: text.trim(),
        id: id.trim(),
        ...(priority !== "" && { priority: parseInt(priority, 10) }),
        ...(leftPaneDisplayOrder !== "" && {
          left_pane_display_order: parseInt(leftPaneDisplayOrder, 10),
        }),
        ...(textColor && { textColor }),
        ...(bgColor && { bgColor }),
        ...(mappedBoValue && { mapped_bo_value: mappedBoValue.trim() }),
        ...(authLetterType && { auth_letter_type: authLetterType.trim() }),
        mark_as_completed_toggle: markAsCompletedToggle,
        agent_mode: agentMode,
        process_clinical_documents: processClinicalDocuments,
      };
      await props.onSave(option);
      closeModal(ModalId.AUTH_OPTIONS_MODAL);
    } catch (error) {
      console.error("Error saving option:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    closeModal(ModalId.AUTH_OPTIONS_MODAL);
    props.onModalClose();
  };

  return (
    <Modal
      dialogId={ModalId.AUTH_OPTIONS_MODAL}
      onSave={handleSave}
      title={props.mode === "add" ? "Add New Auth Option" : "Edit Auth Option"}
      saveButtonText={
        isSaving ? "Saving..." : props.mode === "add" ? "Add" : "Save"
      }
      cancelText="Cancel"
      disableSave={isSaving || !text.trim() || !id.trim()}
      onClose={handleClose}
    >
      <div className="flex flex-col gap-3">
        <TextInput
          id="text"
          label="Display Text"
          required
          onChange={(data) => setText(data.value)}
          error={errors.text || ""}
          resetField={text === ""}
          defaultValue={text}
        />
        <TextInput
          id="id"
          label="ID"
          required
          onChange={(data) => setId(data.value)}
          error={errors.id || ""}
          resetField={id === ""}
          defaultValue={id}
        />
        {props.mode === "edit" && (
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <TextInput
                id="priority"
                label="Priority"
                onChange={(data) => setPriority(data.value)}
                error={errors.priority || ""}
                resetField={priority === ""}
                defaultValue={priority}
                readOnly
              />
            </div>
            <div className="flex flex-col gap-1">
              <TextInput
                id="leftPaneDisplayOrder"
                label="Left Pane Order"
                onChange={(data) => setLeftPaneDisplayOrder(data.value)}
                error={errors.leftPaneDisplayOrder || ""}
                resetField={leftPaneDisplayOrder === ""}
                defaultValue={leftPaneDisplayOrder}
                readOnly
              />
            </div>
          </div>
        )}
        <Select
          id="mappedBoValue"
          label="Mapped BO Value (optional)"
          options={props.boOptions.map((option) => ({
            label: option.label,
            value: option.value,
          }))}
          placeholder="Select BO Value"
          onOptionChange={(option: { value: string }) =>
            setMappedBoValue(option.value)
          }
          defaultValue={mappedBoValue}
        />
        <TextInput
          id="authLetterType"
          label="Auth Letter Type (optional)"
          onChange={(data) => setAuthLetterType(data.value)}
          error=""
          resetField={authLetterType === ""}
          defaultValue={authLetterType}
        />
        <div className="flex flex-row items-center justify-between">
          <div className="text-sm font-medium text-gray-600">Auto Complete</div>
          <div className="flex items-center justify-center">
            <ToggleSwitch
              key={
                markAsCompletedToggle ? "auto-complete-on" : "auto-complete-off"
              }
              id="autoCompleteToggle"
              defaultChecked={markAsCompletedToggle}
              onChangeEmit={(data: { value: boolean }) =>
                setMarkAsCompletedToggle(data.value)
              }
            />
          </div>
        </div>
        <div className="flex flex-row items-center justify-between">
          <div className="text-sm font-medium text-gray-600">
            Assign To RISA Agent
          </div>
          <div className="flex items-center justify-center">
            <ToggleSwitch
              key={agentMode ? "agent-mode-on" : "agent-mode-off"}
              id="agentModeToggle"
              defaultChecked={agentMode}
              onChangeEmit={(data: { value: boolean }) =>
                setAgentMode(data.value)
              }
            />
          </div>
        </div>
        <div className="flex flex-row items-center justify-between">
          <div className="text-sm font-medium text-gray-600">
            Process Clinical Documents
          </div>
          <div className="flex items-center justify-center">
            <ToggleSwitch
              key={
                processClinicalDocuments
                  ? "process-clinical-documents-on"
                  : "process-clinical-documents-off"
              }
              id="processClinicalDocumentsToggle"
              defaultChecked={processClinicalDocuments}
              onChangeEmit={(data: { value: boolean }) =>
                setProcessClinicalDocuments(data.value)
              }
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">
              Text Color
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={textColor || "#000000"}
                onChange={(e) => setTextColor(e.target.value)}
                className="h-8 w-12 cursor-pointer rounded border border-gray-300"
              />
              <input
                type="text"
                value={textColor}
                onChange={(e) => setTextColor(e.target.value)}
                placeholder="#000000"
                className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">
              Background Color
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={bgColor || "#ffffff"}
                onChange={(e) => setBgColor(e.target.value)}
                className="h-8 w-12 cursor-pointer rounded border border-gray-300"
              />
              <input
                type="text"
                value={bgColor}
                onChange={(e) => setBgColor(e.target.value)}
                placeholder="#ffffff"
                className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </div>
          </div>
        </div>
        {(textColor || bgColor) && (
          <div className="mt-2">
            <label className="mr-1 text-xs font-medium text-gray-600">
              Preview
            </label>
            <div
              className="mt-2 inline-block rounded-md px-3 py-1.5 text-sm font-medium"
              style={{
                backgroundColor: bgColor || "#f3f4f6",
                color: textColor || "#374151",
              }}
            >
              {text || "Sample Text"}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AuthOptionsModal;
